"use client";
import React, { type ReactNode, useContext } from "react";
import Tippy, { type TippyProps } from "@tippyjs/react";

import DropdownComponent, { DropdownToggle } from "@stadium/ui/src/components/bootstrap-dropdown";
import { type Placement } from "tippy.js";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";
import { StyledButton } from "@stadium/ui/src/components/button/styled-button";

import "./externalStyles.scss";
import defaultStyles from "./main.module.scss";

export type ToolTipProps = Omit<TippyProps, "theme" | "className"> & {
  heading?: ReactNode;
  isLightTheme?: boolean;
  styles?: ModularStyles;
  containerClass?: string;
  contentContainerClass?: string;
  headingClass?: string;
  childrenWrapperClassName?: string;
  asResponsiveModal?: boolean;
  disableOnResponsive?: boolean;
  onClickOutside?: () => void;
  infoIcon?: boolean;
  responsiveHeading?: ReactChild;
  showCloseIcon?: boolean;
  showCloseButton?: boolean;
  closeButtonText?: ReactNode;
  responsiveContent?: ReactNode;
  iconClassName?: string;
  stopPropagation?: boolean;
  mainContentClass?: string;
};

export const TOOLTIP_LIGHT_ARROW_TOP_THEME = {
  disableOnResponsive: true,
  isLightTheme: true,
  arrow: true,
  placement: "top" as Placement,
};

export const ToolTip = ({
  children,
  visible,
  heading,
  content,
  isLightTheme,
  interactive = false,
  placement = "top-start",
  arrow = false,
  infoIcon = false,
  styles = defaultStyles,
  onClickOutside,
  containerClass = "",
  contentContainerClass = "",
  headingClass = "",
  childrenWrapperClassName = "",
  asResponsiveModal = false,
  disableOnResponsive = false,
  disabled,
  responsiveHeading = heading,
  showCloseIcon,
  showCloseButton = false,
  closeButtonText = "Done",
  responsiveContent = content,
  iconClassName = "",
  stopPropagation = true,
  enablePortal,
  mainContentClass,
  ...rest
}: ToolTipProps) => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  if (isMobileDevice && asResponsiveModal) {
    return (
      <DropdownComponent
        type="bottom-drawer"
        styles={styles}
        enablePortal={enablePortal}
        heading={
          <DropdownToggle showDropdownIcon={false} as="div" showCloseIcon={showCloseIcon}>
            {responsiveHeading}
          </DropdownToggle>
        }
        label={
          <DropdownToggle showDropdownIcon={false}>
            <>
              {infoIcon && <i className={styles.tooltipInfoIcon} />} {children}
            </>
          </DropdownToggle>
        }
        unmountOnClose
      >
        <BottomIconHideWrapper>
          <div className={`${contentContainerClass} ${styles.dropdownContentSm}`}>
            {responsiveContent}
          </div>
          {showCloseButton && (
            <DropdownToggle showDropdownIcon={false} as="div" className="dropdownFooter">
              <StyledButton styleType="primary" className={styles.footerButton} styles={styles}>
                {closeButtonText}
              </StyledButton>
            </DropdownToggle>
          )}
        </BottomIconHideWrapper>
      </DropdownComponent>
    );
  }

  const mainContent = (
    <div className={mainContentClass}>
      {heading && <span className={`${styles.tippyHeading} ${headingClass}`}>{heading}</span>}
      {content && (
        <span className={`${styles.tippyContent} ${contentContainerClass}`}> {content}</span>
      )}
    </div>
  );
  const resolvedDisabled = (disableOnResponsive && isMobileDevice) || disabled;

  return (
    <Tippy
      content={mainContent}
      theme={isLightTheme ? "light" : ""}
      placement={placement}
      arrow={arrow}
      visible={visible}
      interactive={interactive}
      disabled={resolvedDisabled}
      className={`${isLightTheme ? "" : styles.darkTooltipContainer} ${
        styles.tooltipContainer
      } ${containerClass}`}
      {...(onClickOutside && { onClickOutside })}
      {...rest}
      tabIndex={0}
    >
      <span
        className={childrenWrapperClassName}
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
          }
        }}
      >
        {infoIcon && <i className={`${styles.tooltipInfoIcon} ${iconClassName}`} />} {children}
      </span>
    </Tippy>
  );
};

export { Tippy };
export type { Placement } from "tippy.js";

export default ToolTip;
